// frontend/src/components/cspe/AccountRaisonSocialeEditorModal.jsx
//
// Modale d'édition de la raison sociale (accounts.company_name) — utilisée
// côté EG360 pour corriger les cas où Pappers a renvoyé le nom commercial du
// siège (ex: "BOULANGERIE A") au lieu de la dénomination sociale du SIREN
// (ex: "B2I"). La raison sociale apparaît sur les CERFA 3310 (cadre
// IDENTIFICATION) et sur le contrat fournisseur.
//
// Note : accounts.company_name n'est JAMAIS réécrit automatiquement après la
// création initiale par Pappers (vérifié côté CRM et côté EG360), donc cette
// modification est sûre — elle ne sera pas écrasée par un cron.

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiSave } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";

export default function AccountRaisonSocialeEditorModal({
  open,
  onClose,
  client,    // accounts row (avec pappers_data joint)
  onSaved,
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const pappersName = client?.pappers_data?.company_name || "";
  const pappersRepresentant = client?.pappers_data?.representant_nom_complet || "";

  useEffect(() => {
    if (!open) return;
    setValue((client?.company_name || "").trim());
  }, [open, client?.id]);

  if (!open || !client) return null;

  // L'identifiant accounts.id peut être exposé sous client.id (table directe)
  // ou client.account_id (via la vue clients_view). On accepte les 2.
  const accountId = client?.id || client?.account_id || null;
  const trimmed = (value || "").trim();
  const canSave =
    trimmed.length > 0 &&
    trimmed !== (client?.company_name || "").trim() &&
    !!accountId &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("accounts")
        .update({ company_name: trimmed })
        .eq("id", accountId);
      if (error) throw error;
      toast.success("Raison sociale mise à jour");
      if (onSaved) onSaved({ ...client, company_name: trimmed });
      onClose();
    } catch (e) {
      console.error("[AccountRaisonSocialeEditorModal] save error:", e);
      toast.error("Erreur : " + (e.message || "enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-xs font-semibold text-gray-800">
              Raison sociale de l'entreprise
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              SIREN {client?.siren || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 -mt-1"
            aria-label="Fermer"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Raison sociale légale
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: B2I"
              maxLength={200}
              autoFocus
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Apparaît dans le cadre IDENTIFICATION du CERFA 3310 et sur le contrat fournisseur.
            </p>
          </div>

          {pappersName && pappersName !== trimmed && (
            <div className="text-[10px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              <div className="font-medium mb-0.5">Pappers a renvoyé : « {pappersName} »</div>
              <div className="opacity-80">
                Si ce nom correspond à un établissement (enseigne commerciale) plutôt qu'à la dénomination sociale légale, corrige ci-dessus.
              </div>
            </div>
          )}

          {pappersRepresentant && (
            <div className="text-[10px] text-gray-500">
              Représentant légal Pappers : {pappersRepresentant}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white transition-colors ${
              canSave
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            <FiSave className="h-4 w-4 mr-1.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
